import { isEmpty } from '@fruitful/fruitils';
import { isToday, isTomorrow } from 'date-fns';
import * as Linking from 'expo-linking';
import { router } from 'expo-router';
import { useSetAtom } from 'jotai';
import type { ImageSourcePropType } from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';
import {
    BodyLargeRegular,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    EditIcon,
    FootnoteRegular,
    H2Emphasized,
    H6Regular,
    Icon,
    Image,
    isNonMobileWeb,
    Shadows,
    styled,
    VideoIcon,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { useViewEvents } from '../../events/hooks/useViewEvents';
import {
    getFormattedStartTime,
    sessionIsInProgress,
    sessionStartTimeIsWithinNMinutes,
} from '../helpers';
import { useMembershipGuidanceSessionInfo } from '../hooks/useMembershipGuidanceSessionInfo';
import { sessionForDetailsViewAtom } from '../state/GuidanceSessionsAtoms';
import type { GuidanceSession } from '../types/GuidanceSessions';
import { UpcomingSessionCardContext } from '../types/GuidanceSessions';

interface UpcomingSessionProps {
    session: GuidanceSession;
    context: UpcomingSessionCardContext;
    maxWidth?: number;
    shadow?: boolean;
}

const UpcomingSessionCard = ({
    session,
    context,
    maxWidth,
    shadow = true,
}: UpcomingSessionProps) => {
    const { trackViewOnce } = useViewEvents();
    const setSessionForDetailsViewAtom = useSetAtom(sessionForDetailsViewAtom);
    const { startTime, videoConferenceUrl, focus } = session;
    const sessionStartsSoon = sessionStartTimeIsWithinNMinutes(session, 5);
    const sessionInProgress = sessionIsInProgress(session);

    const { getSessionDetails } = useMembershipGuidanceSessionInfo();
    const sessionDetails = getSessionDetails(session.type);
    const { title, description, image } = sessionDetails;
    const focusText = focus?.trim() ?? '';
    const subtitle =
        context === UpcomingSessionCardContext.MY_GUIDE && !isEmpty(focusText)
            ? `"${focusText}"`
            : description;

    const isJoinable = sessionStartsSoon || sessionInProgress;
    const doneRoute =
        context === UpcomingSessionCardContext.HOME
            ? '/home'
            : '/home/my-guide';

    const onCardPress = () => {
        setSessionForDetailsViewAtom(session);
        router.navigate({
            pathname: `/guidance-sessions/session-details`,
            params: {
                guidanceSessionId: session.id,
                doneRoute,
            },
        });
    };
    const onButtonPress = () => {
        void Linking.openURL(videoConferenceUrl);
    };

    trackViewOnce(`${context}/UpcomingSessionCard`);

    return context === UpcomingSessionCardContext.HOME ? (
        <SmallUpcomingSessionCard
            title={title}
            subtitle=""
            startTime={startTime}
            image={image}
            isJoinable={isJoinable}
            onCardPress={onCardPress}
            onButtonPress={onButtonPress}
            maxWidth={maxWidth}
            shadow={shadow}
        />
    ) : (
        <LargeUpcomingSessionCard
            title={title}
            subtitle={subtitle}
            startTime={startTime}
            image={image}
            isJoinable={isJoinable}
            onCardPress={onCardPress}
            onButtonPress={onButtonPress}
            maxWidth={maxWidth}
            shadow={shadow}
        />
    );
};

interface UpcomingSessionCardProps {
    title: string;
    subtitle: string;
    startTime: string;
    image: ImageSourcePropType;
    isJoinable: boolean;
    onCardPress: () => void;
    onButtonPress: () => void;
    maxWidth?: number;
    shadow?: boolean;
}

const LargeUpcomingSessionCard = ({
    title,
    subtitle,
    startTime,
    image,
    isJoinable,
    onCardPress,
    onButtonPress,
    maxWidth,
    shadow = true,
}: UpcomingSessionCardProps) => {
    const titleText = `${title} session`;
    const formattedStartTime = getFormattedStartTime(startTime);

    return (
        <StyledAnimatedView
            style={{
                width: '100%',
                flex: 1,
            }}
            entering={FadeIn.duration(500)}
        >
            <YStack
                padding={20}
                backgroundColor={isJoinable ? '$green300' : '$peach300'}
                borderRadius={20}
                {...(shadow ? Shadows.LARGE : {})}
                cursor="pointer"
                onPress={onCardPress}
                $platform-web={{
                    flexShrink: 1,
                }}
                width="100%"
                maxWidth={maxWidth}
                alignSelf="center"
            >
                <YStack>
                    <BodyLargeRegular>{titleText}</BodyLargeRegular>
                    <H2Emphasized marginVertical={6}>
                        {formattedStartTime}
                    </H2Emphasized>
                    <FootnoteRegular maxWidth="95%" color="$gray1100">
                        {subtitle}
                    </FootnoteRegular>
                </YStack>
                <View marginVertical={24} alignSelf="center" flex={1}>
                    <Image
                        source={image}
                        resizeMode="contain"
                        objectFit="contain"
                        height={166}
                        width={166}
                        $md={{
                            height: 200,
                            width: 200,
                        }}
                    />
                </View>
                {isJoinable && (
                    <YStack
                        width="100%"
                        $sm={{
                            marginTop: 8,
                        }}
                    >
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            tone="positive"
                            size={ButtonSize.LARGE}
                            text="Join session"
                            icon={VideoIcon}
                            onPress={onButtonPress}
                        />
                    </YStack>
                )}
            </YStack>
        </StyledAnimatedView>
    );
};

const SmallUpcomingSessionCard = ({
    title,
    startTime,
    image,
    isJoinable,
    onCardPress,
    onButtonPress,
    maxWidth,
    shadow = true,
}: UpcomingSessionCardProps) => {
    const user = useAuthedUser();
    const guideProfileImage = user.membership?.guide?.profileImageUrl;
    const titleText =
        isNonMobileWeb || title.length < 20 ? `${title} session` : title;
    const formattedStartTime = getFormattedStartTime(startTime);
    const startTimeDate = new Date(startTime);
    const isTodayOrTomorrow =
        isToday(startTimeDate) || isTomorrow(startTimeDate);

    return (
        <YStack
            paddingHorizontal={22}
            paddingVertical={24}
            borderRadius={24}
            gap={16}
            backgroundColor={isJoinable ? '$green300' : '$peach300'}
            width="100%"
            maxWidth={maxWidth}
            alignSelf="center"
            cursor="pointer"
            onPress={onCardPress}
            {...(shadow ? Shadows.LARGE : {})}
        >
            <XStack
                width="100%"
                flex={1}
                gap={12}
                alignItems="center"
                $sm={{ gap: 16 }}
            >
                <View alignSelf="center">
                    {guideProfileImage != null ? (
                        <Image
                            source={{ uri: guideProfileImage }}
                            resizeMode="cover"
                            objectFit="cover"
                            height={48}
                            width={48}
                            borderRadius={25}
                        />
                    ) : (
                        <Image
                            source={image}
                            resizeMode="contain"
                            objectFit="contain"
                            height={50}
                            width={50}
                        />
                    )}
                </View>
                <YStack paddingTop={2} gap={4}>
                    <BodyMediumRegular color="$gray1100">
                        {titleText}
                    </BodyMediumRegular>
                    {isTodayOrTomorrow ? (
                        <H6Regular>{formattedStartTime}</H6Regular>
                    ) : (
                        <BodyLargeRegular>
                            {formattedStartTime}
                        </BodyLargeRegular>
                    )}
                </YStack>
                <XStack flexGrow={1} justifyContent="flex-end">
                    {/* This is here to help indicate that the whole card is pressable (for reschedule/cancel) */}
                    <Icon icon={EditIcon} color="$gray1100" size={20} />
                </XStack>
            </XStack>
            {isJoinable && (
                <Button
                    variant={ButtonVariant.PRIMARY}
                    tone="positive"
                    size={ButtonSize.LARGE}
                    text="Join session"
                    icon={VideoIcon}
                    onPress={onButtonPress}
                />
            )}
        </YStack>
    );
};

export default UpcomingSessionCard;

const StyledAnimatedView = styled(Animated.View);
